import type { FAQType } from '@form-factory/schema';
import {
  createFAQSchema,
  updateFAQSchema,
} from '@form-factory/schema/contracts';
import { createSearchExpression } from '@form-factory/schema/shared';
import { useLingui } from '@lingui/react/macro';
import { getQueryKey } from '@trpc/react-query';
import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Card } from '@/components/card/Card';
import type { DataProviderFactory } from '@/components/combobox/Combobox';
import { ComboboxControl } from '@/components/combobox/ComboboxControl';
import { MetaFields } from '@/components/fields/MetaFields';
import { EntityCRUDForm } from '@/components/form/EntityCRUDForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { trpc, trpcClient } from '@/services/trpc';
import { useBoundStore } from '@/store/store';

import { useFAQMutation } from './useFAQMutation';

export type FAQFormProps = {
  data?: FAQType;
  chatId: string;
};

export const FAQForm = memo(function FAQForm({ data, chatId }: FAQFormProps) {
  const { t } = useLingui();
  const {
    create: { mutateAsync: create },
    remove: { mutateAsync: remove },
    update: { mutateAsync: update },
  } = useFAQMutation();
  const { chat: currentChat } = useBoundStore(
    useShallow(state => ({
      chat: state.chat,
    })),
  );

  const { schema: detailsSchema } = useFieldsSchema(
    [
      {
        name: 'title',
        label: t`Name`,
        required: true,
      },
      {
        name: 'question',
        label: t`Question`,
        required: true,
      },
      {
        name: 'answer',
        label: t`Answer`,
        required: true,
      },
    ],
    [t],
  );

  const faqFormDataProviderFactory = ({
    excludeIds,
    currentChatId,
  }: {
    excludeIds?: string[];
    currentChatId?: string;
  }) => {
    const dataProvider: DataProviderFactory<FAQType, string> = () => ({
      query: async ({ pageSize, search }) => {
        const where = {
          id: { $nin: excludeIds || undefined },
          searchable: {
            $like: search ? createSearchExpression(search) : undefined,
          },
          chat_id: currentChatId || undefined,
        };

        const data = await trpcClient.faqs.findAll.query({
          pagination: {
            pageSize,
          },
          orderBy: { title: 'asc' },
          where,
        });

        return {
          items: data.items,
          total: data.meta.total,
        };
      },

      cacheKeyFactory: params => [
        ...getQueryKey(trpc.faqs.findAll),
        { orderBy: { name: 'asc' } },
        params,
        data?.id,
      ],
    });

    return dataProvider();
  };

  return (
    <EntityCRUDForm
      initialData={data}
      createSchema={createFAQSchema.omit({ chat: true })}
      updateSchema={updateFAQSchema.omit({ chat: true })}
      onCreate={values => create({ ...values, chat: chatId })}
      onUpdate={update}
      onRemove={remove}
      basePath={`/chats/${chatId}/faqs`}
      idParamName='faqId'
    >
      <EntityCRUDForm.Col>
        <Card title={t`Basic details`}>
          <ComboboxControl
            name='parent'
            label={t`Parent`}
            initialValue={data?.parent?.id}
            renderDisplayValue={item => item?.title}
            shouldFilter={false}
            renderSearchValue={item => item?.title}
            dataProviderFactory={() =>
              faqFormDataProviderFactory({
                excludeIds: data?.id ? [data.id] : [],
                currentChatId: currentChat?.id,
              })
            }
          />
          <SchemaFields schema={detailsSchema} />
        </Card>
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col />
      <EntityCRUDForm.Col>
        <MetaFields data={data} />
      </EntityCRUDForm.Col>
    </EntityCRUDForm>
  );
});
